import { useEffect, useState } from "react";
import { DollarSign, Crown, HandCoins, Megaphone, Store, Coins, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { fmtMoney, fmtNumber } from "@/lib/format";
import KPI from "./KPI";

export default function FinancesTab() {
  const auth = useAdminAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    auth.apiGet("/admin/finances")
      .then((r) => setData(r.data))
      .catch(() => toast.error("No se pudieron cargar las finanzas"));
  }, [auth]);

  if (!data) return <p className="text-sm text-ink-mid p-8 text-center" data-testid="finances-loading">Cargando finanzas…</p>;

  const exportPdf = async () => {
    try {
      const { default: jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Ovamx · Reporte financiero", 14, 18);
      doc.setFontSize(9);
      doc.text(new Date().toLocaleString("es"), 14, 24);

      autoTable(doc, {
        startY: 30,
        head: [["Sección", "Métrica", "Valor"]],
        body: [
          ["PRO", "Suscriptores mensuales", String(data.pro.monthly_subs)],
          ["PRO", "Suscriptores anuales", String(data.pro.yearly_subs)],
          ["PRO", "MRR", fmtMoney(data.pro.mrr_usd)],
          ["PRO", "ARR", fmtMoney(data.pro.arr_usd)],
          ["Propinas", "Volumen bruto", fmtMoney(data.tips.gross_volume_usd)],
          ["Propinas", `Comisión ${data.tips.platform_fee_pct}%`, fmtMoney(data.tips.platform_revenue_usd)],
          ["Anuncios", "Ingresos", fmtMoney(data.ads.revenue_usd)],
          ["Anuncios", "Pipeline restante", fmtMoney(data.ads.pipeline_remaining_usd)],
          ["Marketplace", "Ingresos estimados", fmtMoney(data.marketplace.revenue_usd)],
          ["Sparks", "Total redimido", fmtNumber(data.sparks.total_redeemed)],
          ["Sparks", "Pasivo estimado", fmtMoney(data.sparks.estimated_liability_usd)],
          ["TOTAL", "Ingreso mensual estimado", fmtMoney(data.total_monthly_revenue_usd)],
        ],
        theme: "striped",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [14, 22, 38] },
      });
      doc.save(`ovamx_finanzas_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF generado");
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    }
  };

  return (
    <div data-testid="tab-finances" className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Dashboard financiero</p>
        <button
          onClick={exportPdf}
          data-testid="finances-export-pdf"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-ink text-cream hover:bg-coral transition"
        >
          <FileDown className="w-3.5 h-3.5" /> Exportar PDF
        </button>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testid="kpi-total-revenue" icon={DollarSign}
             k={fmtMoney(data.total_monthly_revenue_usd)} v="ingreso mensual estimado" highlight />
        <KPI testid="kpi-mrr" icon={Crown}
             k={fmtMoney(data.pro.mrr_usd)} v="PRO · MRR"
             sub={`${data.pro.monthly_subs} mensual · ${data.pro.yearly_subs} anual`} />
        <KPI testid="kpi-tips" icon={HandCoins}
             k={fmtMoney(data.tips.platform_revenue_usd)} v="propinas · ingreso"
             sub={`Volumen ${fmtMoney(data.tips.gross_volume_usd)} · ${data.tips.platform_fee_pct}% comisión`} />
        <KPI testid="kpi-ads" icon={Megaphone}
             k={fmtMoney(data.ads.revenue_usd)} v="anuncios · ingreso"
             sub={`Pipeline ${fmtMoney(data.ads.pipeline_remaining_usd)}`} />
        <KPI testid="kpi-marketplace" icon={Store}
             k={fmtMoney(data.marketplace.revenue_usd)} v="marketplace (estimado)"
             sub="Piloto · próximamente" />
        <KPI testid="kpi-arr" icon={DollarSign}
             k={fmtMoney(data.pro.arr_usd)} v="PRO · ARR proyectado" />
        <KPI testid="kpi-sparks-liability" icon={Coins}
             k={fmtMoney(data.sparks.estimated_liability_usd)} v="pasivo Sparks"
             sub={`${fmtNumber(data.sparks.total_redeemed)} redimidos`} />
        <KPI testid="kpi-pro-yearly-price" icon={Crown}
             k={fmtMoney(data.pro.yearly_price_usd)} v="precio PRO anual" />
      </section>

      <section className="border hairline bg-cream-card rounded-2xl p-5">
        <h3 className="font-serif text-xl tracking-tight mb-3">Desglose de fuentes</h3>
        <div className="space-y-2">
          {[
            ["PRO suscripciones (MRR)", data.pro.mrr_usd, "bg-coral"],
            ["Comisiones de propinas", data.tips.platform_revenue_usd, "bg-emerald-500"],
            ["Anuncios", data.ads.revenue_usd, "bg-indigo-500"],
            ["Marketplace (estimado)", data.marketplace.revenue_usd, "bg-amber-500"],
          ].map(([label, val, color]) => {
            const total = data.total_monthly_revenue_usd || 1;
            const pct = Math.round((val / total) * 100);
            return (
              <div key={label} data-testid={`finance-source-${label.split(" ")[0].toLowerCase()}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span>{label}</span>
                  <span className="font-mono text-ink-mid">{fmtMoney(val)} · {pct}%</span>
                </div>
                <div className="h-2 bg-cream rounded-full overflow-hidden">
                  <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
